import { useEffect, useState } from 'react';
import type { GapOfferResult, Service } from '@salonomy/types';
import { Button, Modal, Pill, Select, Spinner } from '@salonomy/ui';
import { useOfferGap } from '../../data/queue.js';
import { wallMinutes, wallMinutesToUtcIso } from '../../lib/time.js';
import { clockLabel } from '../../lib/time.js';

export function OfferGapModal({
  date,
  tz,
  target,
  services,
  onClose,
}: {
  date: string;
  tz: string;
  target: { staffId: string; startMin: number; endMin: number };
  services: Service[];
  onClose: () => void;
}) {
  const offer = useOfferGap();
  const [serviceId, setServiceId] = useState<string>('');
  const [result, setResult] = useState<GapOfferResult | null>(null);

  function run(svc: string) {
    offer.mutate(
      {
        staffId: target.staffId,
        startAt: wallMinutesToUtcIso(date, target.startMin, tz),
        endAt: wallMinutesToUtcIso(date, target.endMin, tz),
        serviceId: svc || null,
        maxOffers: 5,
      },
      { onSuccess: setResult },
    );
  }

  // Find matches as soon as the gap is opened.
  useEffect(() => {
    run('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  void wallMinutes; // imported for symmetry with the diary's minute math

  return (
    <Modal
      open
      onClose={onClose}
      title="Offer this gap to the queue"
      width={440}
      footer={
        <Button variant="outline" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="muted">
        {clockLabel(target.startMin)} – {clockLabel(target.endMin)} ·{' '}
        {target.endMin - target.startMin} min open
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <div className="slm-field__label" style={{ marginBottom: 6 }}>
            For service
          </div>
          <Select
            value={serviceId}
            onChange={(e) => {
              setServiceId(e.target.value);
              setResult(null);
              run(e.target.value);
            }}
          >
            <option value="">Any matching service</option>
            {services
              .filter((s) => s.active)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
          </Select>
        </div>
      </div>

      {offer.isPending ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner /> <span className="muted">Matching waiting tickets…</span>
        </div>
      ) : result ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pill tone={result.matchCount ? 'soft' : 'neutral'}>
              {result.matchCount} {result.matchCount === 1 ? 'match' : 'matches'}
            </Pill>
          </div>
          {result.offeredTo.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {result.offeredTo.map((o) => (
                <div
                  key={o.ticketId}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    border: '1px solid var(--slm-border)',
                    borderRadius: 10,
                    padding: '10px 12px',
                    fontSize: 13.5,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{o.clientName}</span>
                  <span className="muted">{o.notified ? 'Notified' : 'Will notify'}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>
              No one waiting fits this gap yet.
            </div>
          )}
          <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.5 }}>
            The SMS/WhatsApp offer fan-out lands with the comms pipeline (Phase 3). For now this
            shows who would be offered the slot.
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
